import { create } from 'zustand';
import { Chat, Message, ChatGroup, ChatMode, ChatLang, AttachmentMeta, Source } from '../types';
import {
  sendMessage,
  simulateStreaming,
  generateChatTitle,
  APIChatResponse,
} from '../services/chatService';
import {
  saveChat,
  loadAllChats,
  deleteChatFromFirestore,
  updateChatTitle,
  deleteAllChats,
  migrateFromLocalStorage,
} from '../services/firestoreChatService';

const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const groupChats = (chats: Chat[]): ChatGroup[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const groups: ChatGroup[] = [];
  const buckets: { label: string; chats: Chat[] }[] = [
    { label: 'Today', chats: [] },
    { label: 'Yesterday', chats: [] },
    { label: 'Previous 7 Days', chats: [] },
    { label: 'Previous 30 Days', chats: [] },
    { label: 'Older', chats: [] },
  ];

  const sorted = [...chats].sort((a, b) => b.updatedAt - a.updatedAt);
  sorted.forEach((chat) => {
    const d = new Date(chat.updatedAt);
    if (d >= today) buckets[0].chats.push(chat);
    else if (d >= yesterday) buckets[1].chats.push(chat);
    else if (d >= lastWeek) buckets[2].chats.push(chat);
    else if (d >= lastMonth) buckets[3].chats.push(chat);
    else buckets[4].chats.push(chat);
  });

  buckets.forEach((b) => {
    if (b.chats.length > 0) groups.push(b);
  });
  return groups;
};

export { groupChats };

// Debounce helper for Firestore saves
let saveTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};
const debouncedSave = (uid: string, chat: Chat, delayMs: number = 1000) => {
  const key = `${uid}_${chat.id}`;
  if (saveTimeouts[key]) clearTimeout(saveTimeouts[key]);
  saveTimeouts[key] = setTimeout(() => {
    saveChat(uid, chat).catch((err) => console.error('Failed to save chat:', err));
    delete saveTimeouts[key];
  }, delayMs);
};

// Force flush all pending saves
const flushPendingSaves = () => {
  // No-op: saves will complete on their timeouts
  // In production you'd track promises and await them
};

interface ChatStore {
  chats: Chat[];
  activeChatId: string | null;
  isGenerating: boolean;
  sidebarOpen: boolean;
  currentMode: ChatMode;
  currentLang: ChatLang;
  cancelStream: (() => void) | null;
  currentUserId: string | null;
  isLoadingChats: boolean;

  createNewChat: () => string;
  setActiveChat: (chatId: string | null) => void;
  sendChatMessage: (content: string, attachments?: File[]) => Promise<void>;
  stopGenerating: () => void;
  deleteChat: (chatId: string) => void;
  renameChat: (chatId: string, newTitle: string) => void;
  clearAllChats: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setCurrentMode: (mode: ChatMode) => void;
  setCurrentLang: (lang: ChatLang) => void;
  loadChatsFromFirestore: (userId: string) => Promise<void>;
  navigateToChat: (chatId: string) => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  chats: [],
  activeChatId: null,
  isGenerating: false,
  sidebarOpen: true,
  currentMode: 'normal',
  currentLang: 'auto',
  cancelStream: null,
  currentUserId: null,
  isLoadingChats: false,

  createNewChat: () => {
    const uid = get().currentUserId;
    const newChat: Chat = {
      id: generateId(),
      title: 'New chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      userId: uid || '',
      sessionId: null,
    };
    set((state) => ({
      chats: [newChat, ...state.chats],
      activeChatId: newChat.id,
    }));

    // Save empty chat to Firestore
    if (uid) {
      saveChat(uid, newChat).catch(console.error);
    }

    // Update URL
    window.history.pushState(null, '', `/chat/${newChat.id}`);

    return newChat.id;
  },

  setActiveChat: (chatId) => {
    set({ activeChatId: chatId });
    if (chatId) {
      window.history.pushState(null, '', `/chat/${chatId}`);
    } else {
      window.history.pushState(null, '', '/');
    }
  },

  navigateToChat: (chatId: string) => {
    const chat = get().chats.find((c) => c.id === chatId);
    if (chat) {
      set({ activeChatId: chatId });
    }
  },

  setCurrentMode: (mode) => set({ currentMode: mode }),
  setCurrentLang: (lang) => set({ currentLang: lang }),

  stopGenerating: () => {
    const { cancelStream } = get();
    if (cancelStream) cancelStream();
    set((state) => ({
      chats: state.chats.map((chat) =>
        chat.id === state.activeChatId
          ? {
              ...chat,
              messages: chat.messages.map((msg) =>
                msg.isStreaming ? { ...msg, isStreaming: false } : msg
              ),
            }
          : chat
      ),
      isGenerating: false,
      cancelStream: null,
    }));
  },

  sendChatMessage: async (content: string, attachments?: File[]) => {
    const state = get();
    let chatId = state.activeChatId;
    const mode = state.currentMode;
    const lang = state.currentLang;
    const uid = state.currentUserId;

    if (!chatId) {
      chatId = get().createNewChat();
    }

    const currentChat = get().chats.find((c) => c.id === chatId);
    const existingSessionId = currentChat?.sessionId || null;

    const attachmentMetas: AttachmentMeta[] = (attachments || []).map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: Date.now(),
      attachments: attachmentMetas.length > 0 ? attachmentMetas : undefined,
      mode,
      lang,
    };

    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
      mode,
      lang,
    };

    set((s) => ({
      chats: s.chats.map((c) =>
        c.id === chatId
          ? { ...c, messages: [...c.messages, userMessage, assistantMessage], updatedAt: Date.now() }
          : c
      ),
      isGenerating: true,
      cancelStream: null,
    }));

    const currentChatId = chatId;

    try {
      const response: APIChatResponse = await sendMessage({
        message: content,
        mode,
        lang,
        sessionId: existingSessionId,
        attachments,
      });

      if (!response.success || !response.data) {
        const errorMsg = response.error?.message || 'An unexpected error occurred.';
        const errorCode = response.error?.code || 'UNKNOWN';

        set((s) => ({
          chats: s.chats.map((c) =>
            c.id === currentChatId
              ? {
                  ...c,
                  messages: c.messages.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, content: `⚠️ **Error** (${errorCode})\n\n${errorMsg}`, isStreaming: false, error: errorCode }
                      : m
                  ),
                }
              : c
          ),
          isGenerating: false,
        }));

        // Save error state to Firestore
        if (uid) {
          const chat = get().chats.find((c) => c.id === currentChatId);
          if (chat) debouncedSave(uid, chat, 500);
        }
        return;
      }

      const fullAnswer = response.data.answer;
      const sources: Source[] = response.data.sources || [];
      const returnedSessionId = response.data.session_id || null;

      // Save session_id
      if (returnedSessionId) {
        set((s) => ({
          chats: s.chats.map((c) =>
            c.id === currentChatId ? { ...c, sessionId: returnedSessionId } : c
          ),
        }));
      }

      const cancel = simulateStreaming(
        fullAnswer,
        (chunk) => {
          set((s) => ({
            chats: s.chats.map((c) =>
              c.id === currentChatId
                ? {
                    ...c,
                    messages: c.messages.map((m) =>
                      m.id === assistantMessageId ? { ...m, content: m.content + chunk } : m
                    ),
                  }
                : c
            ),
          }));
        },
        () => {
          // Streaming done
          set((s) => ({
            chats: s.chats.map((c) =>
              c.id === currentChatId
                ? {
                    ...c,
                    messages: c.messages.map((m) =>
                      m.id === assistantMessageId
                        ? { ...m, isStreaming: false, timestamp: Date.now(), sources: sources.length > 0 ? sources : undefined }
                        : m
                    ),
                    updatedAt: Date.now(),
                  }
                : c
            ),
            isGenerating: false,
            cancelStream: null,
          }));

          // Save completed chat to Firestore
          if (uid) {
            const chat = get().chats.find((c) => c.id === currentChatId);
            if (chat) debouncedSave(uid, chat, 500);
          }
        },
        mode === 'deep' ? 18 : mode === 'research' ? 14 : 10
      );

      set({ cancelStream: cancel });

      // Generate title
      const chatForTitle = get().chats.find((c) => c.id === currentChatId);
      if (chatForTitle && chatForTitle.messages.length <= 2 && chatForTitle.title === 'New chat') {
        const title = await generateChatTitle(content);
        set((s) => ({
          chats: s.chats.map((c) => (c.id === currentChatId ? { ...c, title } : c)),
        }));
        // Save title to Firestore
        if (uid) {
          updateChatTitle(uid, currentChatId, title).catch(console.error);
        }
      }
    } catch (error: any) {
      set((s) => ({
        chats: s.chats.map((c) =>
          c.id === currentChatId
            ? {
                ...c,
                messages: c.messages.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, content: `⚠️ **Connection Error**\n\nUnable to reach the server.`, isStreaming: false, error: 'NETWORK_ERROR' }
                    : m
                ),
              }
            : c
        ),
        isGenerating: false,
        cancelStream: null,
      }));

      if (uid) {
        const chat = get().chats.find((c) => c.id === currentChatId);
        if (chat) debouncedSave(uid, chat, 500);
      }
    }
  },

  deleteChat: (chatId) => {
    const uid = get().currentUserId;
    set((s) => ({
      chats: s.chats.filter((c) => c.id !== chatId),
      activeChatId: s.activeChatId === chatId ? null : s.activeChatId,
    }));

    if (get().activeChatId === null) {
      window.history.pushState(null, '', '/');
    }

    // Delete from Firestore
    if (uid) {
      deleteChatFromFirestore(uid, chatId).catch(console.error);
    }
  },

  renameChat: (chatId, newTitle) => {
    const uid = get().currentUserId;
    set((s) => ({
      chats: s.chats.map((c) => (c.id === chatId ? { ...c, title: newTitle } : c)),
    }));

    if (uid) {
      updateChatTitle(uid, chatId, newTitle).catch(console.error);
    }
  },

  clearAllChats: () => {
    const uid = get().currentUserId;
    set({ chats: [], activeChatId: null });
    window.history.pushState(null, '', '/');

    if (uid) {
      deleteAllChats(uid).catch(console.error);
    }
  },

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  setSidebarOpen: (open) => {
    if (get().sidebarOpen !== open) set({ sidebarOpen: open });
  },

  loadChatsFromFirestore: async (userId: string) => {
    set({ isLoadingChats: true, currentUserId: userId });

    try {
      // One-time migration from localStorage
      const migrated = await migrateFromLocalStorage(userId);
      if (migrated > 0) {
        console.log(`Migrated ${migrated} chats from localStorage to Firestore`);
      }

      // Load from Firestore
      const chats = await loadAllChats(userId);
      set({ chats, isLoadingChats: false });
    } catch (error) {
      console.error('Failed to load chats from Firestore:', error);
      set({ isLoadingChats: false });
    }
  },
}));